import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { keyframes } from '@mui/material/styles';
import { McpIcon } from './icons';
import { useBranding } from '../contexts/BrandingContext';
import { useTranslation } from '../i18n/useTranslation';

export type ProgressStage = 'creating' | 'signing_in';

interface Props {
  stage: ProgressStage;
  organizationName?: string;
}

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const iconReveal = keyframes`
  0%   { opacity: 0; transform: scale(0.6) rotate(-10deg); }
  60%  { opacity: 1; transform: scale(1.06) rotate(4deg); }
  100% { opacity: 1; transform: scale(1) rotate(0); }
`;

const ringSweep = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const checkPop = keyframes`
  0%   { opacity: 0; transform: scale(0.4); }
  70%  { opacity: 1; transform: scale(1.15); }
  100% { opacity: 1; transform: scale(1); }
`;

const connectorGrow = keyframes`
  from { transform: scaleY(0); }
  to   { transform: scaleY(1); }
`;

type StepState = 'done' | 'active' | 'pending';

function StepIndicator({ state }: { state: StepState }) {
  const size = 28;

  if (state === 'done') {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: 'success.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CheckRoundedIcon
          sx={{
            fontSize: 16,
            color: '#fff',
            animation: `${checkPop} 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
          }}
        />
      </Box>
    );
  }

  if (state === 'active') {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: 'rgba(0,0,0,0.08)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '2px solid transparent',
            borderTopColor: 'secondary.main',
            borderRightColor: 'secondary.main',
            animation: `${ringSweep} 1.1s linear infinite`,
          }}
        />
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            opacity: 0.9,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: '2px solid rgba(0,0,0,0.1)',
        bgcolor: 'transparent',
        flexShrink: 0,
      }}
    />
  );
}

function Connector({ done }: { done: boolean }) {
  return (
    <Box
      sx={{
        width: 2,
        height: 20,
        ml: '13px',
        bgcolor: done ? 'success.main' : 'rgba(0,0,0,0.08)',
        transformOrigin: 'top',
        animation: done ? `${connectorGrow} 0.35s ease-out both` : undefined,
      }}
    />
  );
}

function Step({
  state,
  label,
  connectorDone,
  isLast,
}: {
  state: StepState;
  label: string;
  connectorDone?: boolean;
  isLast?: boolean;
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <StepIndicator state={state} />
        <Typography
          sx={{
            fontSize: '0.9rem',
            fontWeight: state === 'pending' ? 400 : 600,
            color: state === 'pending' ? 'text.secondary' : 'text.primary',
            letterSpacing: '-0.005em',
            transition: 'color 200ms ease, font-weight 200ms ease',
          }}
        >
          {label}
        </Typography>
      </Box>
      {!isLast && <Connector done={!!connectorDone} />}
    </Box>
  );
}

export default function SignupProgress({ stage, organizationName }: Props) {
  const branding = useBranding();
  const t = useTranslation();
  const bg = branding.login_background_color || '#fafafa';

  const stageStates: Record<ProgressStage, [StepState, StepState, StepState]> = {
    creating:   ['done', 'active',  'pending'],
    signing_in: ['done', 'done',    'active'],
  };
  const [s1, s2, s3] = stageStates[stage];

  const title = stage === 'creating'
    ? t('signupProgress.title.creating').replace('{org}', organizationName || t('signupProgress.orgFallback'))
    : t('signupProgress.title.signingIn');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: bg,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={(theme) => ({
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: `
            radial-gradient(circle at 18% 22%, ${theme.palette.primary.main}14 0%, transparent 55%),
            radial-gradient(circle at 82% 78%, ${theme.palette.secondary.main}10 0%, transparent 55%)
          `,
        })}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 420,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: '100%',
            bgcolor: 'background.paper',
            borderRadius: (theme) => Math.round(theme.shape.borderRadius * 1.6),
            border: '1px solid rgba(0,0,0,0.08)',
            p: 5,
            textAlign: 'left',
            animation: `${cardIn} 0.5s ease-out both`,
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: (theme) => Math.round(theme.shape.borderRadius * 1.4),
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3.5,
              animation: `${iconReveal} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both`,
            }}
          >
            <McpIcon sx={{ fontSize: 26, color: 'primary.contrastText' }} />
          </Box>

          <Typography
            sx={{
              fontSize: '1.25rem',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'text.primary',
              mb: 0.75,
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9rem',
              color: 'text.secondary',
              lineHeight: 1.55,
              mb: 4,
            }}
          >
            {t('signupProgress.subtitle')}
          </Typography>

          <Box>
            <Step state={s1} label={t('signupProgress.step.accountConnected')} connectorDone={s2 !== 'pending'} />
            <Step state={s2} label={t('signupProgress.step.creatingOrg')} connectorDone={s3 !== 'pending'} />
            <Step state={s3} label={t('signupProgress.step.signingIn')} isLast />
          </Box>

          <Box
            sx={{
              mt: 4,
              pt: 2.5,
              borderTop: '1px solid rgba(0,0,0,0.06)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'secondary.main',
                animation: `${ringSweep} 2.4s linear infinite`,
                boxShadow: (theme) => `0 0 0 3px ${theme.palette.secondary.main}22`,
              }}
            />
            <Typography
              sx={{
                fontSize: '0.78rem',
                color: 'text.secondary',
                letterSpacing: '0.005em',
              }}
            >
              {t('signupProgress.footer')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
